import { useEffect, useRef, type ReactNode } from 'react';
import { Button } from './Button';

/**
 * Centred modal asking to confirm an action that takes effect at once (back office: a price,
 * capacity or status change goes live on the checkout). A native `<dialog>` like `Sheet`.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  loading,
}: {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
    }
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="confirm-title"
      onClose={onCancel}
      className="m-auto w-full max-w-lg rounded-card bg-paper p-0 text-ink backdrop:bg-ink/50"
    >
      <div className="flex flex-col gap-4 p-6">
        <h2 id="confirm-title" className="m-0 font-display text-section font-bold">
          {title}
        </h2>
        <div className="text-[15px]">{children}</div>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" size="md" className="w-auto!" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button size="md" className="w-auto!" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
